import { FC, Fragment, useState } from 'react';
import classNames from 'classnames';
import { Field, useField, useForm } from 'react-final-form';
import dayjs from 'dayjs';
import { AdminElection, SelectedElectionItem } from 'services/adminCandidatesApi';
import { SelectField } from 'components/Forms/SelectField';
import { RACE_DETAIL_CHECKS, RACE_STATUS_OPTIONS } from 'constants/candidateRaces';
import { ElectionSearchSelect } from './ElectionSearchSelect';

export const ElectionPicker: FC = () => {
  const { change } = useForm();
  const {
    input: { value: rawElections },
    meta: { error: electionsError, submitFailed },
  } = useField<SelectedElectionItem[]>('elections_selected', {
    subscription: { value: true, error: true, submitFailed: true },
  });
  const electionsSelected: SelectedElectionItem[] = Array.isArray(rawElections) ? rawElections : [];
  const hasSelection = electionsSelected.length > 0;
  const [activeElectionId, setActiveElectionId] = useState<string | null>(null);

  const handleSelect = (election: AdminElection) => {
    change('elections_selected', [
      ...electionsSelected,
      {
        election_id: election.election_id,
        display_info: election.display_info,
        state: election.state,
        type: election.type,
        election_date: election.election_date,
        status: 'ACTIVE',
      },
    ]);
  };

  const handleDelete = (electionId: string) => {
    change(
      'elections_selected',
      electionsSelected.filter(e => e.election_id !== electionId)
    );
    if (activeElectionId === electionId) setActiveElectionId(null);
  };

  const toggleDetails = (electionId: string) => {
    setActiveElectionId(prev => (prev === electionId ? null : electionId));
  };

  return (
    <>
      <div className="row">
        <div className="col-md-6 padding0">
          <div className="form-input">
            <label htmlFor="election-picker">Election *</label>
            <ElectionSearchSelect
              inputId="election-picker"
              onSelect={handleSelect}
              isDisabled={hasSelection}
              excludeElectionIds={electionsSelected.map(e => e.election_id)}
            />
            {submitFailed && electionsError && <label className="error">{electionsError}</label>}
            <span className="oath-utility gray mt-1">
              {hasSelection ? (
                'Only one election can be linked at a time. Delete it below to choose a different one.'
              ) : (
                <>
                  Can&apos;t find an election? Elections are loaded by the system. Contact{' '}
                  <a href="mailto:support@oath.vote">support@oath.vote</a> for assistance.
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {electionsSelected.length > 0 && (
        <div className="draggable-table-container">
          <table className="draggable-table">
            <tbody>
              {electionsSelected.map((election, index) => {
                const isOpen = activeElectionId === election.election_id;
                return (
                  <Fragment key={election.election_id}>
                    <tr
                      className={classNames({
                        active: isOpen,
                        'expanded-content': isOpen,
                      })}
                    >
                      <td
                        className={classNames('rec-header-info rec active', {
                          expanded: isOpen,
                        })}
                      >
                        <div className="grid">
                          <span className="oath-body inkBlue">{election.display_info}</span>
                          <span className="oath-bodysmall gray">
                            {election.state} · {election.type} ·{' '}
                            {dayjs(election.election_date).format('MMM D, YYYY')}
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="race-status-select">
                            <SelectField
                              name={`elections_selected[${index}].status`}
                              label="Status"
                              options={RACE_STATUS_OPTIONS}
                            />
                          </div>
                          <button
                            type="button"
                            className={classNames('oath-bodysmall link-text color-gray ml-2', {
                              active: isOpen,
                            })}
                            onClick={e => {
                              e.stopPropagation();
                              toggleDetails(election.election_id);
                            }}
                          >
                            {isOpen ? 'Close Details' : 'Add Details'}
                          </button>
                        </div>
                      </td>
                      <td className="delete-td rec">
                        <button
                          type="button"
                          className="oath-bodysmall link-text gray"
                          onClick={e => {
                            e.stopPropagation();
                            handleDelete(election.election_id);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td className="rec-details-info expanded-details rec">
                          <div className="divider mb-2" />
                          <p className="oath-eyebrow inkBlue mb-2">Race details</p>
                          <div className="d-flex election-picker__checks">
                            {RACE_DETAIL_CHECKS.map(({ field, label }) => (
                              <Field
                                key={field}
                                name={`elections_selected[${index}].${field}`}
                                type="checkbox"
                              >
                                {({ input: cbInput }) => (
                                  <div className="form-check form-check-inline mr-3">
                                    <label className="form-check-label oath-bodysmall inkBlue">
                                      <input
                                        type="checkbox"
                                        checked={cbInput.checked}
                                        onChange={cbInput.onChange}
                                      />
                                      <span className="form-check-sign" />
                                      {label}
                                    </label>
                                  </div>
                                )}
                              </Field>
                            ))}
                          </div>
                        </td>
                        <td />
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};
